/**
 * Autosave & Crash Recovery
 *
 * A lightweight safety net on top of the document store: while the app runs,
 * a dirty document periodically produces a compact JSON snapshot (the same
 * .yap/.yate envelope the real Save writes) into localStorage. A clean
 * Save / Open / New clears the snapshot; if the tab or process dies without
 * one, the next launch finds the leftover snapshot and offers to restore it.
 *
 * `startAutosave` is wired by the App bootstrap; the write callback is
 * injected so ProjectFileIO can build the envelope without a circular
 * dependency.
 */

import { useDocumentStore } from '../state/useDocumentStore';
import { useSystemVarsStore } from '../state/useSystemVarsStore';

const SNAPSHOT_KEY = '***';
/** 30 s base tick — the real cadence is governed by the SAVETIME system
 *  variable (minutes; 0 = autosave disabled), checked every tick. */
const AUTOSAVE_TICK_MS = 30_000;
/** localStorage is ~5 MB; skip pathological documents instead of throwing. */
const MAX_SNAPSHOT_CHARS = 3_500_000;

export interface AutosaveSnapshot {
    displayName: string;
    kind: 'project' | 'template';
    savedAt: string;
    envelopeText: string;
}

/** Reads + validates the stored recovery snapshot. */
function readSnapshot(): AutosaveSnapshot | null {
    try {
        const raw = localStorage.getItem(SNAPSHOT_KEY);
        if (!raw) return null;
        const snap = JSON.parse(raw) as AutosaveSnapshot;
        if (!snap?.envelopeText || !snap.displayName || !snap.savedAt) return null;
        return snap;
    } catch {
        return null;
    }
}

/** Writes a snapshot; oversized documents are skipped (real Save still works). */
export function writeSnapshot(snapshot: AutosaveSnapshot): void {
    try {
        if (snapshot.envelopeText.length > MAX_SNAPSHOT_CHARS) return;
        localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch {
        // Quota exceeded or storage unavailable: autosave is best-effort.
    }
}

export function clearSnapshot(): void {
    try {
        localStorage.removeItem(SNAPSHOT_KEY);
    } catch {
        // ignore
    }
}

/** The recovery candidate left by a previous session, if any. */
export function getRecoverySnapshot(): AutosaveSnapshot | null {
    return readSnapshot();
}

let timer: number | null = null;
let lastSnapshotAt = 0;

/** Starts the periodic dirty-document snapshot (idempotent). The SAVETIME
 *  system variable (minutes) drives the cadence; 0 suspends snapshots. */
export function startAutosave(write: () => Promise<void>): void {
    if (timer !== null) return;
    timer = window.setInterval(() => {
        if (!useDocumentStore.getState().isDirty) return;
        const minutes = useSystemVarsStore.getState().SAVETIME ?? 10;
        if (minutes <= 0) return;
        if (Date.now() - lastSnapshotAt < minutes * 60_000) return;
        lastSnapshotAt = Date.now();
        void write();
    }, AUTOSAVE_TICK_MS);
}

// A document becoming clean means it was saved (or discarded/replaced by a
// load) — in every case the recovery snapshot is obsolete. Watching the
// dirty transition centrally covers all save/load paths without each flow
// remembering to clear the slot.
if (typeof window !== 'undefined') {
    let wasDirty = useDocumentStore.getState().isDirty;
    useDocumentStore.subscribe((state) => {
        if (wasDirty && !state.isDirty) clearSnapshot();
        wasDirty = state.isDirty;
    });
}
